import React, { useEffect, useState, useMemo } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import {
  organisation as organisationAPI,
  auth,
  user,
  stroot,
} from '@birdi/js-sdk';
import type { GetWorkspacesResponse } from '@birdi/js-sdk/src/public/organisation';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import { getRelativeTime } from '../utils';
import WorkspaceSVG from '@birdi/icons/workspace-3.svg';
import MapSVG from '@birdi/icons/map.svg';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const DISPLAY_LIMIT = 5;

const WORKSPACE_ICON_COLORS = [
  '#CEE3FF', // midOcean
  '#FFE7AF', // midCanary
  '#BAEDD4', // midFlora
  '#ADADDE', // midMidnight
  '#FEDBB4', // midSunConure
];

type WorkspaceItem = GetWorkspacesResponse['items'][number];

interface UserScopeItem {
  id: string;
  organisationId: string;
  organisationName?: string;
  organisationMenuIcon?: string;
  active?: boolean;
}

export const RecentWorkspaces: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const meta = useMetaSelector();

  const [allItems, setAllItems] = useState<WorkspaceItem[]>([]);
  const [displayCount, setDisplayCount] = useState(DISPLAY_LIMIT);
  const [loading, setLoading] = useState(true);
  const [scopes, setScopes] = useState<UserScopeItem[]>([]);
  const [failedProfilePicUrls, setFailedProfilePicUrls] = useState<Set<string>>(
    () => new Set(),
  );

  const workspaceId = meta.workspace?.id || meta.organisation.id;
  const isWorkspaceAdmin = meta.permissions.includes('workspace:user:create');

  const displayedItems = useMemo(
    () => allItems.slice(0, displayCount),
    [allItems, displayCount],
  );
  const hasMore = allItems.length > displayCount;
  const isExpandDisabled = !displayedItems.length || loading || !hasMore;

  const scopeId = (meta as { scopeId?: string }).scopeId;

  useEffect(() => {
    if (!meta.organisation?.id) {
      setAllItems([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const [workspacesRes, scopesRes] = await Promise.all([
          organisationAPI.getWorkspaces(stroot('recws1'), scopeId ?? undefined),
          user.getScopes(stroot('recws2')),
        ]);
        let items = (workspacesRes.body?.items ?? []) as WorkspaceItem[];
        const scopeList = (scopesRes.body?.items ?? []) as UserScopeItem[];
        const scopeOrgIds = new Set(
          scopeList.map((s) => s.organisationId).filter(Boolean),
        );
        if (!scopeId && scopeOrgIds.size > 0) {
          items = items.filter((w) => scopeOrgIds.has(w.id));
        }
        setAllItems(items);
        setScopes(scopeList);
      } catch (e) {
        console.error('Error fetching recent workspaces:', e);
        setAllItems([]);
        setScopes([]);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id, scopeId]);

  const scopeIdByWorkspaceId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of scopes) {
      if (s.organisationId) map[s.organisationId] = s.id;
    }
    return map;
  }, [scopes]);

  const handleGoToWorkspace = (item: WorkspaceItem) => {
    const sid = scopeIdByWorkspaceId[item.id];
    if (!sid) return;
    posthog?.capture('dashboard recent workspaces go to workspace', {
      workspaceId: item.id,
    });
    auth.switchContext(stroot('recws3'), { scopeId: sid }).then(() => {
      window.location.href = '/';
    });
  };

  const handleManageUsers = (item: WorkspaceItem) => {
    const sid = scopeIdByWorkspaceId[item.id];
    if (!sid) return;
    posthog?.capture('dashboard recent workspaces manage users', {
      workspaceId: item.id,
    });
    auth.switchContext(stroot('recws4'), { scopeId: sid }).then(() => {
      window.location.href = '/admin-users';
    });
  };

  const handleExpand = () => {
    if (isExpandDisabled) return;
    posthog?.capture('dashboard recent workspaces expand');
    setDisplayCount((c) => c + DISPLAY_LIMIT);
  };

  const handleProfilePicError = (url: string) => {
    setFailedProfilePicUrls((prev) => new Set(prev).add(url));
  };

  const hasData = !loading && displayedItems.length > 0;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
      id={suppressIntercomAnchor ? undefined : 'recent-workspaces'}
    >
      {/* Header: same as other widgets */}
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Recent workspaces</span>
      </div>

      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
            font-size: 0.9em;
          `}
        >
          Monitor and manage your workspaces and team activity.
        </div>

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
              color: ${theme.mono50};
            `}
          >
            Loading...
          </div>
        )}

        {!loading && displayedItems.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div css={css`color: ${theme.mono60};`}>
              <strong>Workspaces you belong to will appear here.</strong>
            </div>
          </div>
        )}

        {hasData && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0;
              margin: 0 -1em;
            `}
          >
            {displayedItems.map((item, index) => {
              const bgColor = item.menuIcon
                ? theme.mono0
                : WORKSPACE_ICON_COLORS[index % WORKSPACE_ICON_COLORS.length];
              const canSwitch = !!scopeIdByWorkspaceId[item.id];
              const usersList = Array.isArray(item.users) ? item.users : [];
              return (
                <div
                  key={item.id}
                  css={css`
                    display: grid;
                    grid-template-columns: auto 1fr auto;
                    align-items: start;
                    gap: 0.75em 1em;
                    margin: 0 1em;
                    padding: 1em;
                    border-bottom: 1px solid ${theme.mono30};
                    transition: background 0.15s;
                    &:hover {
                      background: ${theme.mono20};
                    }
                    &:last-child {
                      border-bottom: none;
                    }
                  `}
                >
                  {/* Left: icon/logo box - bigger, less radius, white centered SVG */}
                  <div
                    css={css`
                      width: 75px;
                      height: 75px;
                      border-radius: 3px;
                      background: ${bgColor};
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      overflow: hidden;
                      flex-shrink: 0;
                    `}
                  >
                    {item.menuIcon ? (
                      <img
                        src={item.menuIcon}
                        alt=""
                        css={css`
                          width: 100%;
                          height: 100%;
                          object-fit: cover;
                        `}
                      />
                    ) : (
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          justify-content: center;
                          font-size: 35px;
                        `}
                      >
                        <WorkspaceSVG />
                      </span>
                    )}
                  </div>

                  {/* Center: name, creator, users + maps */}
                  <div
                    css={css`
                      min-width: 0;
                      display: flex;
                      flex-direction: column;
                      gap: 0.25em;
                    `}
                  >
                    <div
                      css={css`
                        font-weight: 700;
                        color: ${theme.midnight};
                        font-size: 1em;
                      `}
                    >
                      {item.name}
                    </div>
                    {item.creator && (
                      <div
                        css={css`
                          font-size: 0.85em;
                          color: ${theme.mono50};
                        `}
                      >
                        Created by{' '}
                        {[item.creator.fname, item.creator.lname]
                          .filter(Boolean)
                          .join(' ') || '—'}
                      </div>
                    )}
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 0.5em 0.5em;
                        font-size: 0.85em;
                        color: ${theme.mono50};
                        @media (max-width: 420px) {
                          flex-direction: column;
                          align-items: flex-start;
                        }
                      `}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          gap: 0.35em;
                        `}
                      >
                        <div
                          css={css`
                            display: inline-flex;
                            align-items: center;
                            position: relative;
                            height: 24px;
                          `}
                        >
                          {usersList.slice(0, 3).map((u, i) => (
                            <div
                              key={i}
                              css={css`
                                width: 24px;
                                height: 24px;
                                border-radius: 50%;
                                background: ${theme.lightOcean};
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                font-weight: 700;
                                font-size: 0.65em;
                                margin-left: ${i === 0 ? 0 : -10}px;
                                border: 2px solid ${theme.mono0};
                                overflow: hidden;
                                color: ${theme.deepOcean};
                              `}
                            >
                              {u.profilePic &&
                              !failedProfilePicUrls.has(u.profilePic) ? (
                                <img
                                  src={u.profilePic}
                                  alt=""
                                  onError={() =>
                                    handleProfilePicError(u.profilePic!)
                                  }
                                  css={css`
                                    width: 100%;
                                    height: 100%;
                                    object-fit: cover;
                                  `}
                                />
                              ) : (
                                <span>
                                  {u.fname?.[0] ?? ''}
                                  {u.lname?.[0] ?? ''}
                                </span>
                              )}
                            </div>
                          ))}
                          {usersList.length > 3 && (
                            <div
                              css={css`
                                margin-left: -10px;
                                width: 24px;
                                height: 24px;
                                border-radius: 50%;
                                background: ${theme.mono0};
                                color: ${theme.flora};
                                font-size: 0.65em;
                                font-weight: 700;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                border: 2px solid ${theme.flora};
                              `}
                            >
                              +{usersList.length - 3}
                            </div>
                          )}
                        </div>
                        {item.totalUsers}{' '}
                        {item.totalUsers === 1 ? 'user' : 'users'}
                      </span>
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          gap: 0.35em;
                        `}
                      >
                        <span
                          css={css`
                            display: inline-flex;
                            width: 14px;
                            height: 14px;
                            color: ${theme.mono50};
                          `}
                        >
                          <MapSVG />
                        </span>
                        {item.totalMaps} maps
                      </span>
                    </div>
                  </div>

                  {/* Right: actions first, time last */}
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-end;
                      gap: 0.35em;
                      font-size: 0.85em;
                    `}
                  >
                    {canSwitch && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleGoToWorkspace(item)}
                          css={css`
                            background: none;
                            border: none;
                            padding: 0.2em 0.35em;
                            margin: 0 -0.35em;
                            cursor: pointer;
                            color: ${theme.deepOcean};
                            font-size: inherit;
                            display: inline-flex;
                            align-items: center;
                            gap: 0.25em;
                            border-radius: 4px;
                            &:hover {
                              background: ${theme.lightOcean};
                            }
                          `}
                        >
                          <span css={css`text-decoration: underline;`}>Go to workspace</span>
                          <span aria-hidden>→</span>
                        </button>
                        {isWorkspaceAdmin && (
                          <button
                            type="button"
                            onClick={() => handleManageUsers(item)}
                            css={css`
                              background: none;
                              border: none;
                              padding: 0.2em 0.35em;
                              margin: 0 -0.35em;
                              cursor: pointer;
                              color: ${theme.deepOcean};
                              font-size: inherit;
                              display: inline-flex;
                              align-items: center;
                              gap: 0.25em;
                              border-radius: 4px;
                              &:hover {
                                background: ${theme.lightOcean};
                              }
                            `}
                          >
                            <span css={css`text-decoration: underline;`}>Manage users</span>
                            <span aria-hidden>→</span>
                          </button>
                        )}
                      </>
                    )}
                    {item.createdAt && (
                      <div
                        css={css`
                          color: ${theme.mono50};
                          white-space: nowrap;
                        `}
                      >
                        {getRelativeTime(item.createdAt)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          type="button"
          disabled={isExpandDisabled}
          onClick={handleExpand}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 6px;
            padding: 0.5em 1em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          Expand
        </button>
      </div>
    </div>
  );
};
